import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import {
  applyOfferImportJob,
  createOfferImportJob,
  fetchOfferImportJob,
  updateOfferImportRow,
} from "../../../../api/offer-import";
import { getOfferCalculationAsync } from "../../../../slices/offer-calculation";
import {
  isOfferFeatureEnabled,
  isOfferNotAuthorized,
  resolveOfferActionErrorMessage,
} from "../utils";

function rowHasValue(value) {
  return value != null && value !== "";
}

/**
 * CSV/XLSX import staging UI (offer_import_v2).
 * Upload → stage/match table → user confirm → apply into calculation items.
 * AI/fuzzy suggestions never auto-apply.
 */
export default function OfferCalculationImport({ calculationId, featureFlags }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const enabled = isOfferFeatureEnabled(featureFlags, "offer_import_v2");

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);

  const counts = useMemo(() => {
    const c = { confirmed: 0, suggested: 0, unmatched: 0, applied: 0, skipped: 0 };
    rows.forEach((r) => {
      if (r.user_confirmed && ["confirmed", "suggested"].includes(r.match_status)) {
        c.confirmed += 1;
      } else if (c[r.match_status] != null) {
        c[r.match_status] += 1;
      }
    });
    return c;
  }, [rows]);

  const applyableCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.user_confirmed &&
          ["confirmed", "suggested"].includes(r.match_status) &&
          !r.applied_item_id
      ).length,
    [rows]
  );

  const columnPresence = useMemo(() => {
    const hasSheet = rows.some((r) => rowHasValue(r.sheet_name));
    const hasChapter = rows.some((r) => rowHasValue(r.chapter_name));
    // Avoid duplicate columns when Excel tags both with the same sheet name.
    const sheetDistinctFromChapter =
      hasSheet &&
      rows.some(
        (r) =>
          rowHasValue(r.sheet_name) &&
          String(r.sheet_name) !== String(r.chapter_name || "")
      );
    return {
      sheet: sheetDistinctFromChapter,
      chapter: hasChapter || (hasSheet && !sheetDistinctFromChapter),
      discount: rows.some((r) => rowHasValue(r.discount)),
      surcharge: rows.some((r) => rowHasValue(r.surcharge)),
      hours: rows.some((r) => rowHasValue(r.hours)),
      price: rows.some((r) => rowHasValue(r.sales_price)),
    };
  }, [rows]);

  if (!enabled || !calculationId) {
    return null;
  }

  const toastImportError = (payload, fallbackKey) => {
    toast.error(resolveOfferActionErrorMessage(t, payload, fallbackKey));
  };

  const isImportFailure = (payload) =>
    isOfferNotAuthorized(payload) ||
    Boolean(payload?.error) ||
    Boolean(
      payload?.errors &&
        (Array.isArray(payload.errors) ? payload.errors.length > 0 : true)
    );

  const handleUpload = async () => {
    if (!file) {
      toast.error(t("offer.calculation.import.file_required"));
      return;
    }
    setBusy(true);
    try {
      const result = await createOfferImportJob(calculationId, file);
      if (isImportFailure(result)) {
        toastImportError(result, "offer.calculation.import.failed");
        return;
      }
      setJob(result.import_job);
      setRows(result.rows || []);
      toast.success(t("offer.calculation.import.upload_success"));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmRow = async (row, confirmed = true) => {
    setBusy(true);
    try {
      const updated = await updateOfferImportRow(calculationId, job.id, row.id, {
        user_confirmed: confirmed,
        matched_product_id: row.matched_product_id,
      });
      if (isImportFailure(updated)) {
        toastImportError(updated, "offer.calculation.import.failed");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } finally {
      setBusy(false);
    }
  };

  const handleSkipRow = async (row) => {
    setBusy(true);
    try {
      const updated = await updateOfferImportRow(calculationId, job.id, row.id, {
        skip: true,
      });
      if (isImportFailure(updated)) {
        toastImportError(updated, "offer.calculation.import.failed");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!job?.id) return;
    setBusy(true);
    try {
      const result = await applyOfferImportJob(calculationId, job.id);
      if (isImportFailure(result)) {
        toastImportError(result, "offer.calculation.import.failed");
        return;
      }
      toast.success(
        t("offer.calculation.import.apply_success", {
          count: result.applied_count ?? 0,
        })
      );
      dispatch(getOfferCalculationAsync(calculationId));
      const refreshed = await fetchOfferImportJob(calculationId, job.id);
      if (!isImportFailure(refreshed)) {
        setJob(refreshed.import_job || result.import_job);
        setRows(refreshed.rows || []);
      } else if (result.import_job) {
        setJob(result.import_job);
      }
    } finally {
      setBusy(false);
    }
  };

  const matchLabel = (status) =>
    t(`offer.calculation.import.match_status.${status}`, { defaultValue: status });

  const formatNum = (value) => {
    if (!rowHasValue(value)) return "—";
    return value;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">
            {t("offer.calculation.import.title")}
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            {t("offer.calculation.import.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full max-w-xs text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700"
          />
          <button
            type="button"
            disabled={busy || !file}
            onClick={handleUpload}
            className="rounded-md bg-[#1463C7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            {t("offer.calculation.import.upload")}
          </button>
          {job?.id ? (
            <button
              type="button"
              disabled={busy || applyableCount < 1}
              onClick={handleApply}
              className="rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
            >
              {t("offer.calculation.import.apply")}
            </button>
          ) : null}
        </div>
      </div>

      {job ? (
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
          <span>
            {t("offer.calculation.import.job_status")}: <strong>{job.status}</strong>
          </span>
          <span>
            {t("offer.calculation.import.counts.confirmed")}: {counts.confirmed}
          </span>
          <span>
            {t("offer.calculation.import.counts.suggested")}: {counts.suggested}
          </span>
          <span>
            {t("offer.calculation.import.counts.unmatched")}: {counts.unmatched}
          </span>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-slate-200 text-slate-500">
              <tr>
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.confirm")}
                </th>
                <th className="py-2 pr-3 font-semibold">#</th>
                {columnPresence.sheet ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.sheet")}
                  </th>
                ) : null}
                {columnPresence.chapter ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.chapter")}
                  </th>
                ) : null}
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.sku")}
                </th>
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.name")}
                </th>
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.qty")}
                </th>
                {columnPresence.price ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.price")}
                  </th>
                ) : null}
                {columnPresence.discount ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.discount")}
                  </th>
                ) : null}
                {columnPresence.surcharge ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.surcharge")}
                  </th>
                ) : null}
                {columnPresence.hours ? (
                  <th className="py-2 pr-3 font-semibold">
                    {t("offer.calculation.import.col.hours")}
                  </th>
                ) : null}
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.match")}
                </th>
                <th className="py-2 pr-3 font-semibold">
                  {t("offer.calculation.import.col.product")}
                </th>
                <th className="py-2 font-semibold">
                  {t("offer.calculation.import.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canConfirm =
                  row.matched_product_id &&
                  !["applied", "skipped"].includes(row.match_status);
                return (
                  <tr key={row.id} className="border-b border-slate-100 align-top">
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        disabled={busy || !canConfirm}
                        checked={Boolean(row.user_confirmed)}
                        onChange={(e) => handleConfirmRow(row, e.target.checked)}
                        aria-label={t("offer.calculation.import.confirm")}
                        className="h-4 w-4 rounded border-slate-300 text-[#1463C7]"
                      />
                    </td>
                    <td className="py-2 pr-3 tabular-nums text-slate-500">
                      {row.row_number}
                    </td>
                    {columnPresence.sheet ? (
                      <td className="py-2 pr-3 text-slate-700">
                        {row.sheet_name || "—"}
                      </td>
                    ) : null}
                    {columnPresence.chapter ? (
                      <td className="py-2 pr-3 text-slate-700">
                        {row.chapter_name || row.sheet_name || "—"}
                      </td>
                    ) : null}
                    <td className="py-2 pr-3 font-mono text-slate-800">
                      {row.external_sku || "—"}
                    </td>
                    <td className="py-2 pr-3 text-slate-800">
                      {row.external_name || "—"}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      {formatNum(row.quantity)}
                    </td>
                    {columnPresence.price ? (
                      <td className="py-2 pr-3 tabular-nums">
                        {formatNum(row.sales_price)}
                      </td>
                    ) : null}
                    {columnPresence.discount ? (
                      <td className="py-2 pr-3 tabular-nums">
                        {formatNum(row.discount)}
                      </td>
                    ) : null}
                    {columnPresence.surcharge ? (
                      <td className="py-2 pr-3 tabular-nums">
                        {formatNum(row.surcharge)}
                      </td>
                    ) : null}
                    {columnPresence.hours ? (
                      <td className="py-2 pr-3 tabular-nums">
                        {formatNum(row.hours)}
                      </td>
                    ) : null}
                    <td className="py-2 pr-3">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                        {matchLabel(row.match_status)}
                      </span>
                      {row.match_method ? (
                        <div className="mt-1 text-[10px] text-slate-400">
                          {row.match_method}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">
                      {row.matched_product_id
                        ? `#${row.matched_product_id}${
                            row.match_candidates?.[0]?.title
                              ? ` · ${row.match_candidates[0].title}`
                              : ""
                          }`
                        : "—"}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {row.match_status === "suggested" && !row.user_confirmed ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleConfirmRow(row, true)}
                            className="rounded border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-50"
                          >
                            {t("offer.calculation.import.confirm")}
                          </button>
                        ) : null}
                        {!["applied", "skipped"].includes(row.match_status) ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleSkipRow(row)}
                            className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
                          >
                            {t("offer.calculation.import.skip")}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
